// src/auth/screens/NoAccessScreen.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../AuthContext.jsx';
import { COLORS, RADII, SHADOW, FONT_SANS } from '../../constants/theme.js';

const shellStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  minHeight: '100vh', background: COLORS.bg, fontFamily: FONT_SANS,
  padding: '24px', boxSizing: 'border-box',
};

const cardStyle = {
  textAlign: 'center', maxWidth: '400px', width: '100%',
  background: COLORS.surface,
  border: `1px solid ${COLORS.border}`,
  borderRadius: RADII.section,
  boxShadow: SHADOW.card,
  padding: '40px',
  boxSizing: 'border-box',
};

const buttonStyle = {
  padding: '12px 24px',
  borderRadius: RADII.control,
  border: 'none',
  background: COLORS.accent,
  color: COLORS.surface,
  fontSize: '14px',
  fontWeight: 700,
  fontFamily: FONT_SANS,
  cursor: 'pointer',
};

// Three variants, in this order of precedence.
//
// A company admin with no active workspace lands here for a DIFFERENT reason from a
// plain member: nobody has to grant them anything — they administer the company, but
// the company owns no ACTIVE workspace (every one retired, or none created yet), so
// "contact your administrator" would point them at themselves. Building an app mode
// with no active workspace is out of scope, so the honest thing is to say what is
// missing and let them reach support. The flag is readable here because a
// non-superadmin with `tenants: []` signs in to phase 'no_tenants' with `customers`
// KEPT — only the tenant list is empty.
function useVariant() {
  const { logout, isSuperadmin, isCustomerAdmin } = useAuth();
  const { t } = useTranslation('auth');
  const navigate = useNavigate();

  if (isSuperadmin) {
    return {
      glyph: '🛡️',
      title: t('noAccess.superadminTitle'),
      message: t('noAccess.superadminMessage'),
      label: t('noAccess.superadminButton'),
      onClick: () => navigate('/admin/users', { replace: true }),
    };
  }
  if (isCustomerAdmin) {
    return {
      glyph: '🏢',
      title: t('noAccess.companyNoWorkspaceTitle'),
      message: t('noAccess.companyNoWorkspaceMessage'),
      label: t('noAccess.backToLogin'),
      onClick: logout,
    };
  }
  return {
    glyph: '🔒',
    title: t('noAccess.title'),
    message: t('noAccess.message'),
    label: t('noAccess.backToLogin'),
    onClick: logout,
  };
}

export default function NoAccessScreen() {
  const { glyph, title, message, label, onClick } = useVariant();

  return (
    <div style={shellStyle}>
      <div style={cardStyle}>
        <p style={{ fontSize: '32px', margin: '0 0 16px' }}>{glyph}</p>
        <h2 style={{ color: COLORS.ink, fontSize: '20px', fontWeight: 700, margin: '0 0 8px' }}>{title}</h2>
        <p style={{ color: COLORS.textTertiary, fontSize: '14px', margin: '0 0 28px' }}>{message}</p>
        <button onClick={onClick} style={buttonStyle}>{label}</button>
      </div>
    </div>
  );
}
